<template>
  <div class="page-wrapper">
    <div class="page-inner">
      <div class="deliveries-header">
        <div class="container">
          <div class="deliveries-header__inner">
            <h1 class="deliveries-header__title">Deliveries</h1>
            
            <div class="deliveries-toolbar">
              <div class="deliveries-filters">
                <div class="filter-group filter-group--dates">
                  <button
                    v-for="date in dateOptions"
                    :key="date.value"
                    class="filter-btn filter-btn--date"
                    :class="{ 'filter-btn--active': currentDate === date.value && !customDateRange }"
                    @click="setDateFilter(date.value)"
                  >
                    {{ date.label }}
                  </button>
                  <date-picker
                    ref="datePicker"
                    v-model="customDateRange"
                    range
                    format="DD.MM.YYYY"
                    value-type="format"
                    @change="onCustomDateChange"
                  >
                    <template #input>
                      <button
                        class="filter-btn filter-btn--date filter-btn--custom"
                        :class="{ 'filter-btn--active': customDateRange }"
                        type="button"
                      >
                        <i class="ri-calendar-line"></i>
                        Custom
                      </button>
                    </template>
                  </date-picker>
                </div>

                <div class="filter-divider"></div>

                <div class="filter-group filter-group--status">
                  <button
                    v-for="status in statusOptions"
                    :key="status.value"
                    class="filter-btn filter-btn--status"
                    :class="{ 
                      'filter-btn--active': currentStatus === status.value,
                      'filter-btn--green': status.value === 2 && currentStatus === 2,
                      'filter-btn--red': status.value === 0 && currentStatus === 0
                    }"
                    @click="setStatusFilter(status.value)"
                  >
                    {{ status.label }}
                  </button>
                </div>

                <div class="filter-search">
                  <i class="ri-search-line"></i>
                  <input
                    id="searchDelivery"
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search..."
                    @input="handleSearch"
                  />
                  <button v-if="searchQuery" class="filter-search__clear" @click="clearSearch">
                    <i class="ri-close-line"></i>
                  </button>
                </div>
              </div>

              <button class="btn-primary" @click="showAddDelivery">
                <i class="ri-add-line"></i>
                Add Delivery
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="deliveries-filters--mobile">
        <div class="search-input--mobile">
          <i class="ri-search-line"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search..."
            @input="handleSearch"
          />
        </div>
        <button class="filter-toggle" @click="filterShowMobile = true">
          <i class="ri-filter-3-line"></i>
          <span v-if="filterCount" class="filter-toggle__badge">{{ filterCount }}</span>
        </button>
        <button class="btn-primary btn-primary--icon" @click="showAddDeliveryMobile">
          <i class="ri-add-line"></i>
        </button>
      </div>

      <div class="table">
        <Delivery
          v-for="delivery in deliveries"
          :key="delivery.id"
          :info="delivery"
          @click.native="showDelivery(delivery)"
        />
      </div>
    </div>

    <el-drawer
      class="el--mobile"
      :visible.sync="filterShowMobile"
      direction="btt"
      :show-close="false"
      :with-header="false"
      destroy-on-close
      size="auto"
    >
      <div class="mobile-filters">
        <div class="mobile-filters__header">
          <span>Filters</span>
          <button class="icon-button" @click="filterShowMobile = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="mobile-filters__body">
          <div class="mobile-filters__section">
            <label class="mobile-filters__label">Date</label>
            <div class="mobile-filters__buttons">
              <button
                v-for="date in dateOptions"
                :key="date.value"
                class="mobile-filter-btn"
                :class="{ 'mobile-filter-btn--active': currentDate === date.value }"
                @click="setDateFilter(date.value)"
              >
                {{ date.label }}
              </button>
            </div>
          </div>
          <div class="mobile-filters__section">
            <label class="mobile-filters__label">Status</label>
            <div class="mobile-filters__buttons">
              <button
                v-for="status in statusOptions"
                :key="status.value"
                class="mobile-filter-btn"
                :class="{ 
                  'mobile-filter-btn--active': currentStatus === status.value,
                  'mobile-filter-btn--green': status.value === 2 && currentStatus === 2,
                  'mobile-filter-btn--red': status.value === 0 && currentStatus === 0
                }"
                @click="setStatusFilter(status.value)"
              >
                {{ status.label }}
              </button>
            </div>
          </div>
          <button v-if="hasFiltersApplied" class="mobile-filter-btn mobile-filter-btn--reset" @click="clearAllFilters">
            <i class="ri-refresh-line"></i>
            Reset Filters
          </button>
        </div>
      </div>
    </el-drawer>

    <el-drawer
      class="el--mobile"
      :visible.sync="deliveryShow"
      direction="btt"
      :show-close="false"
      :with-header="false"
      destroy-on-close
      size="64%"
    >
      <DeliveryInfo :info="activeDelivery" />
    </el-drawer>

    <infinite-loading v-if="!isLoading && hasMoreResults" @infinite="loadMore"></infinite-loading>
  </div>
</template>

<script>
import Delivery from './Delivery'
import DeliveryInfo from './DeliveryPopupMobile'
import InfiniteLoading from 'vue-infinite-loading'
import _ from 'lodash'
import Popup from './DeliveryPopup'
import DatePicker from 'vue2-datepicker'

export default {
  name: 'Deliveries',
  components: { Delivery, DeliveryInfo, InfiniteLoading, DatePicker },
  data() {
    return {
      isLoading: false,
      activeDelivery: {},
      filterShowMobile: false,
      deliveryShow: false,
      searchQuery: '',
      currentDate: 'today',
      currentStatus: null,
      customDateRange: null,
      dateOptions: [
        { value: 'today', label: 'Today' },
        { value: 'tomorrow', label: 'Tomorrow' },
        { value: 'thisWeek', label: 'This Week' }
      ],
      statusOptions: [
        { value: null, label: 'All' },
        { value: 2, label: 'Done' },
        { value: 0, label: 'Not Done' }
      ]
    }
  },
  computed: {
    deliveries() {
      return this.$store.getters['deliveries/deliveries']
    },
    hasMoreResults() {
      return this.deliveries.length < this.totalCount && this.page > 1
    },
    totalCount() {
      return this.$store.getters['deliveries/pagination'].total
    },
    page() {
      return this.$store.getters['deliveries/page']
    },
    hasFiltersApplied() {
      return this.currentDate !== 'today' || this.currentStatus !== null || this.searchQuery !== '' || this.customDateRange
    },
    filterCount() {
      let count = 0
      if (this.currentDate !== 'today' && !this.customDateRange) count++
      if (this.customDateRange) count++
      if (this.currentStatus !== null) count++
      if (this.searchQuery) count++
      return count
    }
  },
  created() {
    this.initializeFilters()
    this.fetchDeliveries()
  },
  mounted() {
    this._keyListener = (e) => {
      if (e.key === 'f' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        document.querySelector('#searchDelivery')?.focus()
      }
    }
    document.addEventListener('keydown', this._keyListener)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._keyListener)
  },
  methods: {
    initializeFilters() {
      const stored = this.$store.getters['deliveries/filterParamsLocal']
      if (stored) {
        if (stored.date && stored.date.length > 0) {
          const dateVal = typeof stored.date[0] === 'object' ? stored.date[0].value : stored.date[0]
          this.currentDate = dateVal || 'today'
        }
        if (stored.status && stored.status.value !== undefined) {
          this.currentStatus = stored.status.value === 2 ? null : stored.status.value
        }
        if (stored.name) {
          this.searchQuery = stored.name
        }
      }
    },
    buildFilterParams() {
      const params = {}
      
      if (this.customDateRange && this.customDateRange[0]) {
        params['filters[dateRange][from]'] = this.customDateRange[0]
        params['filters[dateRange][till]'] = this.customDateRange[1]
      } else if (this.currentDate) {
        params['filters[date][]'] = this.currentDate
      }
      
      if (this.currentStatus !== null) {
        params['filters[status]'] = this.currentStatus
      }
      
      return params
    },
    saveFiltersToStore() {
      const local = {
        date: this.currentDate ? [{ value: this.currentDate, label: this.currentDate }] : [],
        status: { value: this.currentStatus === null ? 2 : this.currentStatus },
        name: this.searchQuery || null,
        range: [null, null],
        dateRange: null
      }
      this.$store.commit('deliveries/filterParamsLocal', local)
      this.$store.commit('deliveries/filterParamsSend', this.buildFilterParams())
      this.$store.commit('deliveries/searchParams', this.searchQuery || null)
    },
    fetchDeliveries() {
      this.saveFiltersToStore()
      this.$store.dispatch('deliveries/fetchFresh')
    },
    setDateFilter(value) {
      this.customDateRange = null
      if (this.currentDate === value) {
        this.currentDate = null
      } else {
        this.currentDate = value
      }
      this.fetchDeliveries()
    },
    onCustomDateChange(range) {
      if (range && range[0]) {
        this.currentDate = null
        this.customDateRange = range
      } else {
        this.customDateRange = null
        this.currentDate = 'today'
      }
      this.fetchDeliveries()
    },
    setStatusFilter(value) {
      this.currentStatus = value
      this.fetchDeliveries()
    },
    handleSearch: _.debounce(function() {
      this.fetchDeliveries()
    }, 400),
    clearSearch() {
      this.searchQuery = ''
      this.fetchDeliveries()
    },
    clearAllFilters() {
      this.currentDate = 'today'
      this.currentStatus = null
      this.searchQuery = ''
      this.customDateRange = null
      this.fetchDeliveries()
    },
    async loadMore() {
      if (this.isLoading) return
      this.isLoading = true
      try {
        await this.$store.dispatch('deliveries/fetch')
      } finally {
        this.isLoading = false
      }
    },
    showAddDelivery() {
      this.$root.$emit('ShowSidebar', 'AddDelivery')
    },
    showAddDeliveryMobile() {
      this.$root.$emit('ShowSidebar', 'AddDeliveryMobile')
    },
    showDelivery(info) {
      const mobile = document.documentElement.clientWidth <= 768
      if (mobile) {
        this.activeDelivery = info
        this.deliveryShow = true
      } else {
        this.$modal.show(
          Popup,
          { info },
          { ...this.$modal.styles, name: 'DeliveryPopup', height: 'auto' },
          {
            'before-open': this.$modal.open,
            'before-close': this.$modal.close
          }
        )
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/assets/styles/_variables.scss';

.deliveries-header {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 14px 0;
  position: sticky;
  top: 0;
  z-index: 10;

  &__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  &__title {
    font-size: 20px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
  }
}

.deliveries-toolbar {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  justify-content: flex-end;

  @media (max-width: 1024px) {
    display: none;
  }
}

.deliveries-filters {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-btn {
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  text-align: center;
  flex-shrink: 0;
  min-width: 70px;
  height: 28px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  &--date {
    min-width: 78px;
  }

  &--custom {
    min-width: 88px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    i {
      font-size: 14px;
      flex-shrink: 0;
    }
  }

  &--status {
    min-width: 75px;
  }

  &:hover:not(&--active) {
    background: #e5e7eb;
    border-color: #d1d5db;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(2, 144, 252, 0.2);
  }

  &--active {
    color: #fff;
    background: #0290FC;
    border-color: #0290FC;
  }

  &--green.filter-btn--active {
    background: #10b981;
    border-color: #10b981;
  }

  &--red.filter-btn--active {
    background: #ef4444;
    border-color: #ef4444;
  }
}

::v-deep .mx-datepicker {
  width: auto;
  flex-shrink: 0;

  .mx-icon-calendar,
  .mx-icon-clear {
    display: none;
  }
}

.filter-divider {
  width: 1px;
  height: 28px;
  background: #d1d5db;
  margin: 0 12px;
}

.filter-search {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  height: 28px;
  min-width: 140px;
  flex: 1;
  max-width: 240px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  transition: all 0.15s ease;
  margin-left: 12px;

  &:focus-within {
    background: #fff;
    border-color: #0290FC;
    box-shadow: 0 0 0 2px rgba(2, 144, 252, 0.15);
  }

  i {
    color: #9ca3af;
    font-size: 14px;
    flex-shrink: 0;
  }

  input {
    flex: 1;
    min-width: 0;
    padding: 0;
    font-size: 13px;
    color: #374151;
    background: transparent;
    border: none;
    outline: none;

    &::placeholder {
      color: #9ca3af;
    }
  }

  &__clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    background: #d1d5db;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition: background 0.15s ease;
    flex-shrink: 0;

    i {
      font-size: 12px;
      color: #fff;
    }

    &:hover {
      background: #9ca3af;
    }
  }
}

.filter-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #9ca3af;
  background: #f3f4f6;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 18px;
  }

  &:hover {
    color: #ef4444;
    background: #fef2f2;
  }
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 14px;
  height: 28px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: #0290FC;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  i {
    font-size: 16px;
  }

  &:hover {
    background: #0180e0;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(2, 144, 252, 0.3);
  }

  &--icon {
    width: 40px;
    height: 40px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

.deliveries-filters--mobile {
  display: none;
  padding: 12px 16px;
  gap: 10px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 1024px) {
    display: flex;
  }
}

.search-input--mobile {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 40px;
  background: #f3f4f6;
  border-radius: 8px;

  i {
    color: #9ca3af;
    font-size: 16px;
  }

  input {
    flex: 1;
    padding: 0;
    font-size: 14px;
    color: #374151;
    background: transparent;
    border: none;
    outline: none;

    &::placeholder {
      color: #9ca3af;
    }
  }
}

.filter-toggle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  i {
    font-size: 20px;
    color: #6b7280;
  }

  &__badge {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    font-size: 11px;
    font-weight: 600;
    color: #fff;
    background: #0290FC;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.mobile-filters {
  background: #fff;
  border-radius: 20px 20px 0 0;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;

    span {
      font-size: 18px;
      font-weight: 600;
      color: #1a1a2e;
    }
  }

  &__body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.mobile-filter-btn {
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s ease;

  &--active {
    color: #fff;
    background: #0290FC;
    border-color: #0290FC;
  }

  &--green.mobile-filter-btn--active {
    background: #10b981;
    border-color: #10b981;
  }

  &--red.mobile-filter-btn--active {
    background: #ef4444;
    border-color: #ef4444;
  }

  &--reset {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin-top: 10px;
    color: #6b7280;
    background: #f3f4f6;
    border-color: #e5e7eb;

    i {
      font-size: 16px;
    }
  }
}

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  i {
    font-size: 20px;
  }

  &:hover {
    background: #f3f4f6;
  }
}

.table {
  padding: 0;
}

@media (max-width: 768px) {
  .table {
    padding: 0 16px;
  }
}
</style>
